import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { CheckCircle, XCircle, DollarSign, Eye, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_VARIANT = { SUBMITTED: 'default', APPROVED: 'default', PAID: 'success', REJECTED: 'destructive' };

/**
 * Coordinator / Admin / Finance page: review submitted expenses.
 * Route guard: requires `expenses.approve` OR `expenses.mark_paid`.
 */
export default function ExpenseApprovalPage() {
  const [tab, setTab] = useState('SUBMITTED'); // SUBMITTED | APPROVED
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [receiptBlobUrl, setReceiptBlobUrl] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/expenses/pending', { params: { status: tab } });
      setItems(res.data.data || []);
    } catch (e) {
      toast.error('Failed to load expenses');
    } finally { setLoading(false); setRefreshing(false); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onVis = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', load);
    document.addEventListener('visibilitychange', onVis);
    return () => { window.removeEventListener('focus', load); document.removeEventListener('visibilitychange', onVis); };
  }, [load]);

  useEffect(() => () => { if (receiptBlobUrl) URL.revokeObjectURL(receiptBlobUrl); }, [receiptBlobUrl]);

  const openDetail = async (row) => {
    setSelected(row); setNotes('');
    if (receiptBlobUrl) { URL.revokeObjectURL(receiptBlobUrl); setReceiptBlobUrl(null); }
    if (row.has_receipt) {
      try {
        const res = await api.get(`/expenses/${row.id}/receipt`, { responseType: 'blob' });
        setReceiptBlobUrl(URL.createObjectURL(res.data));
      } catch (_) { /* silent */ }
    }
  };

  const act = async (action) => {
    if (!selected) return;
    setBusy(true);
    try {
      if (action === 'MARK_PAID') {
        await api.post(`/expenses/${selected.id}/mark-paid`);
        toast.success('Marked paid');
      } else {
        await api.post(`/expenses/${selected.id}/approve`, { action, notes: notes || null });
        toast.success(action === 'APPROVE' ? 'Approved' : 'Rejected');
      }
      setSelected(null); setNotes('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.error?.message || 'Action failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="expense-approval-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Expense Approvals</h1>
          <p className="text-muted-foreground mt-1">Review submitted expense claims</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { setRefreshing(true); load(); }} disabled={refreshing} data-testid="expense-approval-refresh">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      <div className="flex gap-2">
        {['SUBMITTED', 'APPROVED'].map((s) => (
          <Button key={s} variant={tab === s ? 'default' : 'outline'} size="sm" onClick={() => setTab(s)} data-testid={`expense-tab-${s.toLowerCase()}`}>
            {s === 'SUBMITTED' ? 'Awaiting approval' : 'Approved — awaiting payment'}
          </Button>
        ))}
      </div>

      {loading ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">Loading…</CardContent></Card>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <DollarSign className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">No {tab.toLowerCase()} expenses to review.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((e) => (
            <Card key={e.id} data-testid={`expense-row-${e.id}`}>
              <CardHeader className="pb-2 flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-base">{e.submitted_by_name || 'Support Worker'} · {e.currency} {e.amount?.toFixed?.(2) ?? e.amount}</CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">{e.category_code} · {e.expense_date} · {e.description}</p>
                </div>
                <Badge variant={STATUS_VARIANT[e.status]}>{e.status}</Badge>
              </CardHeader>
              <CardContent className="flex justify-between items-center">
                <p className="text-xs text-muted-foreground">{e.has_receipt ? 'Receipt attached' : 'NO receipt'}</p>
                <Button size="sm" onClick={() => openDetail(e)} data-testid={`expense-open-${e.id}`}>
                  <Eye className="h-4 w-4 mr-1.5" />Review
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) setSelected(null); }}>
        <DialogContent data-testid="expense-review-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Review Expense</DialogTitle>
            <DialogDescription>Approve, reject or mark paid</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-3 text-sm">
              <div><span className="text-muted-foreground">Submitted by: </span>{selected.submitted_by_name || selected.submitted_by}</div>
              <div><span className="text-muted-foreground">Category: </span>{selected.category_code}</div>
              <div><span className="text-muted-foreground">Amount: </span>{selected.currency} {selected.amount?.toFixed?.(2) ?? selected.amount}</div>
              <div><span className="text-muted-foreground">Date: </span>{selected.expense_date}</div>
              <div><span className="text-muted-foreground">Description: </span>{selected.description}</div>
              {selected.notes && <div><span className="text-muted-foreground">Notes: </span>{selected.notes}</div>}
              {selected.has_receipt ? (
                <div>
                  <p className="text-muted-foreground mb-1">Receipt:</p>
                  {receiptBlobUrl ? (
                    selected.receipt_content_type?.startsWith('image/')
                      ? <img src={receiptBlobUrl} alt="receipt" className="rounded border max-h-96 object-contain" />
                      : <a href={receiptBlobUrl} target="_blank" rel="noreferrer" className="text-primary underline">Open receipt</a>
                  ) : <p className="text-xs">Loading…</p>}
                </div>
              ) : <p className="text-xs text-destructive">No receipt attached.</p>}
              {selected.status === 'SUBMITTED' && (
                <div>
                  <label className="text-sm text-muted-foreground">Reviewer note (optional)</label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="expense-review-notes" />
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            {selected?.status === 'SUBMITTED' && (
              <>
                <Button variant="destructive" onClick={() => act('REJECT')} disabled={busy} data-testid="expense-review-reject">
                  <XCircle className="h-4 w-4 mr-1.5" />Reject
                </Button>
                <Button onClick={() => act('APPROVE')} disabled={busy} data-testid="expense-review-approve">
                  <CheckCircle className="h-4 w-4 mr-1.5" />Approve
                </Button>
              </>
            )}
            {selected?.status === 'APPROVED' && (
              <Button onClick={() => act('MARK_PAID')} disabled={busy} data-testid="expense-review-paid">
                <DollarSign className="h-4 w-4 mr-1.5" />Mark paid
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
